import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CheckCircle2, ChevronDown, Info, XCircle } from "lucide-react";
import MoreInfoButton from "@/components/more-info-button";
import StyledTooltip from "@/components/styled-tooltip";
import { Terminal } from "lucide-react";
import { DnsRecordsResponse } from "@/types/postman.types";
import { cn } from "@/lib/utils";

const EmailHealthOpenRelay = ({
  relaycheck,
}: {
  relaycheck: DnsRecordsResponse["relaycheck"];
}) => {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold text-foreground">
          Open relay check
        </h2>
        <MoreInfoButton slug="open-relay" />
      </div>
      <div className="grid gap-4">
        <Collapsible defaultOpen>
          <Card>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-teal-500" />
                    Session Transcript
                    <StyledTooltip description="The session transcript shows the detailed SMTP communication logs between your mail server and the receiving server.">
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </StyledTooltip>
                  </CardTitle>
                  <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 [&[data-state=open]>svg]:rotate-180" />
                </div>
                <CardDescription className="self-start">
                  Detailed SMTP communication logs
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="font-mono text-sm bg-slate-950 text-slate-50 rounded-lg overflow-hidden">
                    {/* Terminal Header */}
                    <div className="bg-slate-900 px-4 py-2 border-b border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <span className="text-xs text-slate-400">
                          SMTP Session
                        </span>
                      </div>
                    </div>

                    {/* Terminal Content */}
                    <div className="p-4 space-y-1">
                      {relaycheck?.sessionLines.map((line, index) => {
                        const [prefix, ...rest] = line.split(": ");
                        const content = rest.join(": ");

                        return (
                          <div
                            key={index}
                            className="flex items-start gap-3 group"
                          >
                            <div
                              className={cn(
                                "min-w-[24px] text-center rounded px-1",
                                prefix === "S"
                                  ? "bg-emerald-500/10 text-emerald-400"
                                  : "bg-blue-500/10 text-blue-400"
                              )}
                            >
                              {prefix}
                            </div>
                            <div className="flex-1">
                              <span
                                className={cn(
                                  "font-mono",
                                  prefix === "S"
                                    ? "text-slate-200"
                                    : "text-slate-300"
                                )}
                              >
                                {content}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Test Results Section */}
                  <div className="border rounded-lg">
                    <div className="p-4 border-b bg-muted/50 flex items-center gap-2">
                      <h3 className="font-medium">Test Results</h3>
                      <StyledTooltip description="These results show whether your mail server is configured as an open relay (which allows anyone to send emails through your server) and if it supports STARTTLS encryption for secure email transmission.">
                        <Info className="w-3 h-3 text-muted-foreground" />
                      </StyledTooltip>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "p-2 rounded-full",
                            relaycheck?.openRelay
                              ? "bg-red-100 text-red-600 dark:bg-red-900/20"
                              : "bg-green-100 text-green-600 dark:bg-green-900/20"
                          )}
                        >
                          {relaycheck?.openRelay ? (
                            <XCircle className="w-5 h-5" />
                          ) : (
                            <CheckCircle2 className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {relaycheck?.openRelay
                              ? "Open Relay Detected"
                              : "No Open Relay Detected"}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {relaycheck?.openRelay
                              ? "Your mail server may be vulnerable to unauthorized relay attempts."
                              : "Your mail server is properly configured and does not allow unauthorized relay attempts."}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 space-y-4">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              relaycheck?.startTlsSupported
                                ? "bg-green-500"
                                : "bg-yellow-500"
                            )}
                          ></div>
                          <span className="text-sm">
                            STARTTLS:{" "}
                            {relaycheck?.startTlsSupported
                              ? "Enabled"
                              : "Not enabled"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              relaycheck?.errored
                                ? "bg-red-500"
                                : "bg-green-500"
                            )}
                          ></div>
                          <span className="text-sm">
                            Test Status:{" "}
                            {relaycheck?.errored
                              ? "Test encountered errors"
                              : "Test completed successfully"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </div>
  );
};

export default EmailHealthOpenRelay;
